import React, { useState, useMemo } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';

const HamburgerMenu = ({ menuItems, onMenuItemClick, currentLocation, isOpen: externalIsOpen, onToggle, hideButton = false }) => {
  const isMobile = useIsMobile();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return menuItems;
    return menuItems.filter((i) => (i.label || '').toLowerCase().includes(q));
  }, [menuItems, search]);

  if (!isMobile) return null;

  // Use external state if provided, otherwise use internal
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggle !== undefined ? onToggle : setInternalIsOpen;

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleItemClick = (item) => {
    onMenuItemClick(item);
    setIsOpen(false);
  };

  return (
    <>
      {/* Hamburger Button (skip when hideButton — Header renders own integrated button) */}
      {!hideButton && (
      <button
        onClick={toggleMenu}
        data-testid="hamburger-menu-button"
        style={{
          position: 'fixed',
          top: '16px',
          left: '16px',
          width: '48px',
          height: '48px',
          background: 'rgba(212, 175, 55, 0.15)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          cursor: 'pointer',
          zIndex: 'calc(var(--z-sidebar) + 1)',
          transition: 'all 0.2s ease',
          padding: 0
        }}
        onTouchStart={(e) => {
          e.currentTarget.style.transform = 'scale(0.95)';
        }}
        onTouchEnd={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <div
          style={{
            width: '20px',
            height: '2px',
            background: 'var(--gold)',
            transition: 'all 0.3s ease',
            transform: isOpen ? 'rotate(45deg) translateY(7px)' : 'none'
          }}
        />
        <div
          style={{
            width: '20px',
            height: '2px',
            background: 'var(--gold)',
            transition: 'all 0.3s ease',
            opacity: isOpen ? 0 : 1
          }}
        />
        <div
          style={{
            width: '20px',
            height: '2px',
            background: 'var(--gold)',
            transition: 'all 0.3s ease',
            transform: isOpen ? 'rotate(-45deg) translateY(-7px)' : 'none'
          }}
        />
      </button>
      )}

      {/* Menu Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={toggleMenu}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 'var(--z-sidebar-overlay)',
              animation: 'fadeIn 0.2s ease'
            }}
          />

          {/* Menu Panel */}
          <div
            data-testid="hamburger-menu-panel"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 'min(92vw, 360px)',
              height: '100vh',
              background: 'var(--bg-surface)',
              borderRight: '1px solid var(--border-dim)',
              zIndex: 'var(--z-sidebar)',
              overflowY: 'auto',
              animation: 'slideInFromLeft 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
              boxShadow: '4px 0 40px rgba(0, 0, 0, 0.6)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingTop: 'env(safe-area-inset-top)',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '18px 20px',
                borderBottom: '1px solid var(--border-dim)',
                background: 'var(--bg-surface-elevated)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                position: 'sticky', top: 0, zIndex: 2,
              }}
            >
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Menu
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  {currentLocation === 'krakow' ? '🏰 Kraków' : currentLocation === 'grodek' ? '🌊 Gródek' : '🏔️ Nowy Sącz'}
                </div>
              </div>
              <button
                type="button"
                onClick={toggleMenu}
                aria-label="Zamknij menu"
                data-testid="hamburger-menu-close"
                style={{
                  width: 40, height: 40, borderRadius: 999,
                  background: 'var(--border-dim)', color: 'var(--text-primary)',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 700,
                }}
              >✕</button>
            </div>

            {/* 🔍 SEARCH */}
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-dim)' }}>
              <div style={{ position: 'relative' }}>
              <input
                type="search"
                name="hamburger_module_search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Szukaj modułu..."
                data-testid="mobile-menu-search"
                style={{
                  width: '100%', padding: '10px 30px 10px 12px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-dim)', background: 'var(--bg-surface-elevated)',
                  color: 'var(--text-primary)', fontSize: '14px', outline: 'none',
                }}
              />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    aria-label="Wyczyść"
                    data-testid="mobile-menu-search-clear"
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      width: 24, height: 24, borderRadius: '50%', border: 'none',
                      background: 'var(--border-dim)', color: 'var(--text-primary)',
                      cursor: 'pointer', fontSize: 13,
                    }}
                  >✕</button>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div style={{ padding: '8px' }}>
              {filteredItems.length === 0 && (
                <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
                  Brak modułów pasujących do &bdquo;{search}&rdquo;
                </div>
              )}
              {filteredItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleItemClick(item)}
                  data-testid={`menu-item-${item.id}`}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: item.active ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                    border: 'none',
                    borderLeft: item.active ? '3px solid var(--gold)' : '3px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    color: item.active ? 'var(--gold)' : 'var(--text-primary)',
                    fontSize: '15px',
                    fontWeight: item.active ? '600' : '400',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '4px',
                    transition: 'all 0.2s ease',
                    minHeight: 'var(--touch-target-min)'
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.background = item.active ? 'rgba(212, 175, 55, 0.1)' : 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      data-testid={`menu-item-${item.id}-badge`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '22px',
                        height: '22px',
                        padding: '0 7px',
                        borderRadius: '999px',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 700,
                        boxShadow: '0 0 0 2px rgba(239,68,68,0.25)'
                      }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default HamburgerMenu;
